import React, { useContext, useEffect, useMemo } from 'react';

import type { Task } from 'types/interfaces';
import { getEmployeePickerEmptyMessage } from 'utils';
import { buildEmployeeAssignmentRulesParamsFromTask } from 'utils/taskEmployeeAssignmentRules';

import { TaskExecutorPicker } from 'ui/shared/TaskExecutorPicker';

import { buildDeptCacheKey } from '../lib/distributionHelpers';
import { DistributionRowContext } from '../rowContext';

export const ExecutorPickerCell: React.FC<{ task: Task }> = ({ task }) => {
  const ctx = useContext(DistributionRowContext);
  const taskId = Number(task.id);
  const deptKey = task.department?.key;
  const deptId =
    deptKey !== undefined && deptKey !== '' && deptKey !== null
      ? Number(deptKey)
      : NaN;
  const showAll = ctx ? ctx.showAllByTaskId[taskId] ?? true : true;
  const hasDept = !isNaN(deptId) && deptId > 0;
  const assignmentRulesParams = useMemo(
    () => buildEmployeeAssignmentRulesParamsFromTask(task, deptId),
    [deptId, task]
  );

  useEffect(() => {
    if (ctx && hasDept) {
      ctx.ensureEmployees(deptId, showAll, assignmentRulesParams);
    }
  }, [ctx, hasDept, deptId, showAll, assignmentRulesParams]);

  if (!ctx) return null;

  const selected = ctx.manualExecutorsByTaskId[taskId] ?? [];
  const cacheKey = hasDept
    ? buildDeptCacheKey(deptId, showAll, assignmentRulesParams)
    : '';
  const allCacheKey = hasDept ? buildDeptCacheKey(deptId, true) : '';
  const rawFromCache = cacheKey ? ctx.employeesByKey[cacheKey] : undefined;
  const pickerError = cacheKey ? ctx.errorsByKey[cacheKey] : undefined;
  const allRawFromCache = allCacheKey
    ? ctx.employeesByKey[allCacheKey]
    : undefined;
  const raw = rawFromCache ?? [];
  const allRaw = allRawFromCache ?? [];
  const isLoading = hasDept
    ? !!ctx.inFlight[cacheKey] ||
      rawFromCache === undefined ||
      (!showAll && allRawFromCache === undefined)
    : false;

  return (
    <TaskExecutorPicker
      task={task}
      rawExecutors={raw}
      selectedExecutors={selected}
      onSelectionChange={(e) => ctx.handleManualExecutorsChange(taskId, e)}
      showAllEmployees={showAll}
      onToggleShowAll={() => ctx.setShowAllForTask(taskId, !showAll)}
      pickerKey={`dist-task-employees-${taskId}-${deptId}`}
      emptyMessage={
        pickerError ??
        getEmployeePickerEmptyMessage({
          hasDepartment: hasDept,
          showAllEmployees: showAll,
          currentEmployeesCount: raw.length,
          allDepartmentEmployeesCount: allRaw.length,
          isLoading,
          noDepartmentMessage: 'Наряд без участка'
        })
      }
      minWidth={360}
      overlayWidthMultiplier={1.25}
    />
  );
};
